import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import NavBar from '../components/NavBar/NavBar';
import AdminSidebar from '../components/AdminSidebar/AdminSidebar';
import { useSelector } from 'react-redux';
import TokenRefreshDialog from '../components/TokenRefreshDialog';
import SessionExpiryDialog from '../components/feedback/SessionExpiryDialog';
import { startTokenExpirationCheck, stopTokenExpirationCheck, refreshToken, startAutoTokenRefresh } from '../utils/tokenRefreshUtils';
import { canPerformWaiterActions, resolveStaffRoles } from '../utils/staffRoles';

const AdminLayout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const [showRefreshDialog, setShowRefreshDialog] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const authState = useSelector((state) => state.auth);
  const roles = resolveStaffRoles(authState);
  const isAdmin = roles?.includes('admin');
  const canUseWaiterTools = canPerformWaiterActions(roles);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('menuLanguage');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    if (isAuthenticated) {
      if (canUseWaiterTools) {
        // Waiters and admins: auto-refresh token every 30 minutes in background
        startAutoTokenRefresh();
      } else {
        // For other roles: show dialog when token is about to expire
        startTokenExpirationCheck(() => setShowRefreshDialog(true));
      }
    } else {
      stopTokenExpirationCheck();
    }
    return () => stopTokenExpirationCheck();
  }, [isAuthenticated, canUseWaiterTools]);

  const handleRefreshToken = async () => {
    const success = await refreshToken();
    if (success) {
      setShowRefreshDialog(false);
    }
  };

  if (isLoading) {
    return <div className="app loading" role="status">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="admin-layout" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#1e2225' }}>
      <NavBar menuOpen={menuOpen} setMenuOpen={setMenuOpen} showAdminLinks={!isAdmin} />

      <TokenRefreshDialog
        open={showRefreshDialog}
        onRefresh={handleRefreshToken}
        onClose={() => setShowRefreshDialog(false)}
      />

      <SessionExpiryDialog />

      {/* Main row: sidebar (left) + content (right) */}
      <div style={{ display: 'flex', flex: 1, backgroundColor: '#1e2225' }}>
        {isAdmin && <AdminSidebar mobileOpen={menuOpen} onMobileClose={() => setMenuOpen(false)} />}
        <div className="admin-content" style={{ flex: 1, backgroundColor: '#1e2225' }}>
          {React.isValidElement(children)
            ? React.cloneElement(children, { menuOpen })
            : children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;